import { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "../../../components/ui/button";
import { Label } from "../../../components/ui/label";
import { Textarea } from "../../../components/ui/textarea";
import { X } from "lucide-react";
import { compareNames } from "../utils/attendanceHelpers";
import {
  useAddCourseGuestEnrollmentMutation,
  useGetCourseGuestCandidatesQuery,
} from "../api/lessonAttendanceEndpoints";
import { supabase, saveLessonAttendanceBulkRPC } from "../../../lib/supabase";
import { useAuth } from "../../../contexts/AuthContext";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../../components/ui/alert-dialog";

export interface TagesgastCardProps {
  /** Course lesson ID. Card renders nothing without one. */
  lessonId?: string;
  /** Module / course name of the target lesson — used in the auto-move
   *  note text when a guest is taken out of their original module. */
  lessonName?: string;
  /** Students already in the active roster — filtered out of candidates. */
  enrolledIds: Set<string>;
  /** Refresh handler called after a successful add. */
  reloadBundle: () => Promise<void>;
  /**
   * Teacher user_id override for kiosk callers. The card otherwise
   * reads `userProfile.id` from useAuth(), which on a paired kiosk
   * is null — both the auto-move call (recordedBy) and the
   * resolution-row insert (resolver_staff_id) silently no-op.
   * Passing the chip-login session's user_id keeps both working.
   */
  userIdOverride?: string | null;
}

/**
 * Reusable "Schüler:in als Tagesgast hinzufügen" card.
 *
 * Used both inside the live `EditSection` (after attendance opens for editing)
 * and inside `FuturePreviewContent` (so a teacher can pre-stage guests before
 * the 10-min editing window opens). Self-contained: owns its expand/collapse
 * state, candidate query, search box, multi-select chips, and reason textarea.
 */
// Audit #25: a selected guest carries optional conflict info so the submit
// path knows when to write the "Intern beurlaubt" trail on the original
// module's lesson.
type SelectedGuest = {
  id: string;
  name: string;
  currentLessonId?: string;
  currentModuleName?: string;
};

export function TagesgastCard({
  lessonId,
  lessonName,
  enrolledIds,
  reloadBundle,
  userIdOverride,
}: TagesgastCardProps) {
  const { schoolId, userProfile } = useAuth() as any;
  // Prefer the kiosk-supplied override when present; fall back to
  // useAuth() for the authenticated browser-tab path.
  const effectiveUserId: string | null =
    userIdOverride ?? (userProfile?.id ?? null);
  const [addCourseGuestEnrollment, { isLoading: isAddingGuest }] =
    useAddCourseGuestEnrollmentMutation();

  const [showCard, setShowCard] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [selectedGuests, setSelectedGuests] = useState<SelectedGuest[]>([]);
  const [guestReason, setGuestReason] = useState("");
  const [guestPickerPrimed, setGuestPickerPrimed] = useState(false);
  const [guestPickerOpen, setGuestPickerOpen] = useState(false);
  const [localGuestError, setLocalGuestError] = useState<string | null>(null);
  // Audit #25: when the submit click is intercepted by a conflict warning,
  // we stash the picked guests here and defer the actual handler until the
  // user clicks "Trotzdem hinzufügen" in the AlertDialog.
  const [conflictConfirmOpen, setConflictConfirmOpen] = useState(false);
  const guestPickerRef = useRef<HTMLDivElement>(null);

  const {
    data: classData,
    isFetching: loadingClassCandidates,
    error: classError,
  } = useGetCourseGuestCandidatesQuery(
    { lessonId: (lessonId as string) || "", query: null },
    { skip: !lessonId || !guestPickerPrimed },
  );

  const searchQuery =
    guestPickerPrimed && searchText.trim().length >= 2
      ? searchText.trim()
      : null;
  const {
    data: searchData,
    isFetching: loadingSearchCandidates,
    error: searchError,
  } = useGetCourseGuestCandidatesQuery(
    { lessonId: (lessonId as string) || "", query: searchQuery },
    { skip: !lessonId || !searchQuery },
  );

  const guestError =
    localGuestError ||
    (classError as any)?.data?.error ||
    (searchError as any)?.data?.error ||
    null;
  const loadingCandidates = loadingClassCandidates || loadingSearchCandidates;

  const selectedGuestIds = useMemo(
    () => new Set(selectedGuests.map((g) => g.id)),
    [selectedGuests],
  );

  // Audit #24/#25: carry className + current_module info through from the
  // RPC so the dropdown can both disambiguate identical names AND warn the
  // teacher when the student already has a module at this same time slot.
  const classCandidates = useMemo(() => {
    if (!classData?.class_candidates) return [];
    return classData.class_candidates
      .map((p: any) => ({
        id: String(p.id),
        name: String(p.name || "").trim(),
        className: p.class_name ? String(p.class_name).trim() : "",
        currentLessonId: p.current_lesson_id
          ? String(p.current_lesson_id)
          : undefined,
        currentModuleName: p.current_module_name
          ? String(p.current_module_name).trim()
          : undefined,
      }))
      .filter(
        (p) =>
          p.id &&
          p.name &&
          !enrolledIds.has(p.id) &&
          !selectedGuestIds.has(p.id),
      )
      .sort((a, b) => compareNames(a.name, b.name));
  }, [classData, enrolledIds, selectedGuestIds]);

  const searchCandidates = useMemo(() => {
    if (!searchData?.search_candidates) return [];
    return searchData.search_candidates
      .map((u: any) => ({
        id: String(u.id),
        name: String(u.name || "").trim(),
        className: u.class_name ? String(u.class_name).trim() : "",
        currentLessonId: u.current_lesson_id
          ? String(u.current_lesson_id)
          : undefined,
        currentModuleName: u.current_module_name
          ? String(u.current_module_name).trim()
          : undefined,
      }))
      .filter(
        (u) =>
          u.id &&
          u.name &&
          !enrolledIds.has(u.id) &&
          !selectedGuestIds.has(u.id),
      )
      .sort((a, b) => compareNames(a.name, b.name))
      .slice(0, 20);
  }, [searchData, enrolledIds, selectedGuestIds]);

  const filteredClassCandidates = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    if (!q) return classCandidates;
    return classCandidates.filter((c) => c.name.toLowerCase().includes(q));
  }, [classCandidates, searchText]);

  const primeGuestPicker = () => {
    if (!guestPickerPrimed) setGuestPickerPrimed(true);
  };

  const toggleGuestSelection = (candidate: SelectedGuest) => {
    setSelectedGuests((prev) =>
      prev.some((g) => g.id === candidate.id) ? prev : [...prev, candidate],
    );
    setSearchText("");
  };
  const removeFromSelection = (id: string) => {
    setSelectedGuests((prev) => prev.filter((g) => g.id !== id));
  };

  const canSubmitGuests =
    selectedGuests.length > 0 &&
    !!guestReason.trim() &&
    !!lessonId &&
    !isAddingGuest;

  // Audit #25: guests with a conflicting current_module need to be moved
  // out of that module on confirm. Surface the list to drive the
  // AlertDialog body and the post-add absent_excused write.
  const conflictGuests = useMemo(
    () => selectedGuests.filter((g) => g.currentLessonId && g.currentModuleName),
    [selectedGuests],
  );

  /** Submit-button click. Opens the conflict dialog if needed; otherwise
   *  proceeds to the actual add. */
  const requestAddGuestStudents = () => {
    if (!canSubmitGuests) return;
    if (conflictGuests.length > 0) {
      setConflictConfirmOpen(true);
      return;
    }
    void handleAddGuestStudents();
  };

  const handleAddGuestStudents = async () => {
    if (!canSubmitGuests || !lessonId) return;
    setLocalGuestError(null);
    const studentIds = selectedGuests.map((g) => g.id);
    const reason = guestReason.trim();
    try {
      const result = await addCourseGuestEnrollment({
        lessonId,
        studentIds,
        reason,
      }).unwrap();

      // Audit #25: for each picked guest who already had a module at this
      // time slot, write absent_excused on the ORIGINAL lesson with a
      // standardised "Intern beurlaubt, Gast im Modul: X" note. Reuses
      // save_lesson_attendance_bulk so the daily-log recompute, klärung
      // surface, and Klassenbuch trail all stay consistent — same path a
      // teacher would take if they marked it manually.
      // Soft-fail per student: a failed move log is annoying but the new
      // enrollment is the source of truth and shouldn't be rolled back.
      if (conflictGuests.length > 0) {
        for (const g of conflictGuests) {
          if (!g.currentLessonId || !schoolId) continue;
          try {
            const moduleLabel = lessonName?.trim() || "anderes Modul";
            await saveLessonAttendanceBulkRPC({
              lessonId: g.currentLessonId,
              schoolId,
              attendance: [
                {
                  student_id: g.id,
                  status: "absent_excused",
                  note: `Intern beurlaubt, Gast im Modul: ${moduleLabel}`,
                },
              ],
              recordedBy: effectiveUserId,
            });
            // Tell the FlexBand overview the OTHER module's roster also
            // changed so its module card refreshes without a manual reload.
            try {
              window.dispatchEvent(
                new CustomEvent("flex:attendance:saved", {
                  detail: { lessonId: g.currentLessonId },
                }),
              );
            } catch {}
          } catch (moveErr) {
            console.warn(
              "[TagesgastCard] auto-move (absent_excused on original) failed",
              { studentId: g.id, originalLessonId: g.currentLessonId, moveErr },
            );
          }
        }
      }

      // Mirror voraus-Klärung's send-to-module pattern: also log a
      // flex_overview_issue_resolutions row per student so the FlexBand
      // module card's "Gastkinder:" line picks them up. The buildGuestMap
      // parser requires student_class_name on the payload, so we look it
      // up alongside the names. Soft-fail: the enrollment is the source
      // of truth, the resolution row is for UI surfacing only.
      try {
        if (effectiveUserId && schoolId && studentIds.length > 0) {
          // Resolve current class for each student in one round-trip.
          const today = new Date();
          const todayIso = today.toLocaleDateString("en-CA");
          const { data: cls } = await supabase
            .from("class_enrollments" as any)
            .select(
              "student_id, class_id, structure_classes!inner(name)" as any,
            )
            .in("student_id", studentIds as any)
            .eq("school_id", schoolId as any)
            .lte("started_at", todayIso)
            .or(`ended_at.is.null,ended_at.gte.${todayIso}`);
          const classByStudent = new Map<string, string>();
          for (const row of (cls || []) as any[]) {
            const sid = String(row?.student_id || "");
            const name = row?.structure_classes?.name || "";
            if (sid && name) classByStudent.set(sid, String(name));
          }

          const now = new Date();
          const hh = String(now.getHours()).padStart(2, "0");
          const mm = String(now.getMinutes()).padStart(2, "0");
          const ss = String(now.getSeconds()).padStart(2, "0");
          const tzMinutes = -now.getTimezoneOffset();
          const sign = tzMinutes >= 0 ? "+" : "-";
          const tzH = String(Math.floor(Math.abs(tzMinutes) / 60)).padStart(2, "0");
          const tzM = String(Math.abs(tzMinutes) % 60).padStart(2, "0");
          // Stamp resolved_at on today (the live-edit Tagesgast flow is
          // always for "right now"). The voraus-Klärung path stamps the
          // selected day; here the lesson is the current attendance.
          const resolvedAt = `${todayIso}T${hh}:${mm}:${ss}${sign}${tzH}:${tzM}`;

          const enrollmentByStudent = new Map<string, string>();
          for (const r of (result as any)?.results || []) {
            if (r?.student_id && r?.enrollment_id) {
              enrollmentByStudent.set(String(r.student_id), String(r.enrollment_id));
            }
          }

          // Resolution row semantics (matches morning_report_klaerung /
          // klaerung_at_checkout): `lesson_id` is the ORIGINAL lesson the
          // kid was scheduled in (the Klärung's origin — typically a
          // cancelled module). The destination lives in
          // `action_payload.to_lesson_id`. Readers like
          // fn_flexband_get_sidebar_state and fn_class_teacher_klaerung_report
          // join `f.lesson_id = cancelled.lesson_id` to dismiss the open
          // Klärung once a resolution exists; before this fix we wrote the
          // destination into `lesson_id` and Laith's "send to module"
          // resolution never satisfied that filter, so the checkout dialog
          // kept re-asking.
          //
          // For guests without a conflicting original lesson (g.currentLessonId
          // undefined) we leave lesson_id NULL — there's no Klärung to dismiss,
          // and buildGuestMapFromHistory still picks up the Gastkinder line
          // via payload.to_lesson_id which we keep in place.
          const rows = selectedGuests.map((g) => ({
            school_id: schoolId,
            student_id: g.id,
            resolver_staff_id: effectiveUserId,
            action: "anwesend",
            action_payload: {
              source: "voraus_klaerung_send_to_module",
              to_lesson_id: lessonId,
              student_name: g.name,
              student_class_name: classByStudent.get(g.id) || "",
              reason,
              enrollment_id: enrollmentByStudent.get(g.id) || null,
            },
            note: reason,
            resolved_at: resolvedAt,
            lesson_id: g.currentLessonId ?? null,
          }));

          if (rows.length > 0) {
            await supabase
              .from("flex_overview_issue_resolutions" as any)
              .insert(rows as any);
          }
        }
      } catch (err) {
        console.warn("Tagesgast saved but flex-resolution log failed", err);
      }

      setSelectedGuests([]);
      setGuestReason("");
      setSearchText("");
      setShowCard(false);
      await reloadBundle();

      // Audit #21: notify the FlexBand overview that the lesson's roster
      // changed so its `Gastkinder:` line refreshes without a full page
      // reload. FlexbandOverview already listens for `flex:attendance:saved`
      // (see ModuleCard's onSaved → window.dispatchEvent) and re-fetches
      // the sidebar bundle that feeds buildGuestMapFromHistory. The
      // realtime channel for flex_overview_issue_resolutions is unreliable
      // for these inserts (per the existing comment in FlexbandOverview
      // around onAdvanceClaerungSaved), so the explicit window event is
      // the load-bearing path here too.
      try {
        window.dispatchEvent(
          new CustomEvent("flex:attendance:saved", {
            detail: { lessonId },
          }),
        );
      } catch {}
    } catch (err: any) {
      setLocalGuestError(
        err?.error || err?.message || "Gast konnte nicht hinzugefügt werden.",
      );
    }
  };

  // Close picker dropdown on outside click.
  useEffect(() => {
    if (!guestPickerOpen) return;
    const onClick = (e: MouseEvent) => {
      if (
        guestPickerRef.current &&
        !guestPickerRef.current.contains(e.target as Node)
      ) {
        setGuestPickerOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [guestPickerOpen]);

  // Reset picker state whenever the card is collapsed.
  useEffect(() => {
    if (!showCard) {
      setSearchText("");
      setSelectedGuests([]);
      setGuestReason("");
      setLocalGuestError(null);
      setGuestPickerOpen(false);
    }
  }, [showCard]);

  return (
    <div className="space-y-2">
      <div className="flex justify-center lg:justify-end">
        <Button
          type="button"
          variant="outline"
          onClick={() => setShowCard((v) => !v)}
        >
          <span className="lg:hidden">
            {showCard ? "Schließen" : "+ Tagesgast"}
          </span>
          <span className="hidden lg:inline">
            {showCard ? "Tagesgast schließen" : "Tagesgast hinzufügen"}
          </span>
        </Button>
      </div>

      {showCard && (
        <div className="rounded-md border border-blue-300 bg-blue-50 p-3 space-y-3">
          <div className="text-sm font-medium text-blue-900">
            Schüler:in als Tagesgast hinzufügen
          </div>
          <div className="text-xs text-blue-800">
            Auswahl aus Klassenbezug, optional mit schulweiter Suche. Der Gast
            ist nur für diesen Tag eingeschrieben.
          </div>

          {selectedGuests.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {selectedGuests.map((g) => (
                <span
                  key={g.id}
                  className="inline-flex items-center gap-1 rounded-full bg-blue-600 text-white text-xs px-2 py-1"
                >
                  {g.name}
                  <button
                    type="button"
                    onClick={() => removeFromSelection(g.id)}
                    aria-label={`${g.name} aus Auswahl entfernen`}
                    className="hover:bg-blue-700 rounded-full p-0.5"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
          )}

          <div ref={guestPickerRef} className="relative">
            <input
              type="text"
              value={searchText}
              onChange={(e) => {
                setSearchText(e.target.value);
                primeGuestPicker();
                setGuestPickerOpen(true);
              }}
              onFocus={() => {
                primeGuestPicker();
                setGuestPickerOpen(true);
              }}
              onClick={() => {
                primeGuestPicker();
                setGuestPickerOpen(true);
              }}
              placeholder="Name eingeben oder Liste öffnen…"
              className="w-full border border-blue-200 rounded-md px-2 py-1.5 text-sm bg-white"
              disabled={isAddingGuest}
            />
            {guestPickerOpen && (
              <div className="absolute z-50 mt-1 w-full max-h-[260px] overflow-y-auto rounded-md border border-blue-200 bg-white shadow-lg">
                {loadingCandidates && (
                  <div className="px-3 py-2 text-xs text-gray-500">
                    Lade Schüler:innen…
                  </div>
                )}
                {!loadingCandidates &&
                  filteredClassCandidates.length === 0 &&
                  searchCandidates.length === 0 && (
                    <div className="px-3 py-2 text-xs text-gray-500">
                      {searchText.trim().length >= 2
                        ? "Keine Treffer"
                        : "Tippe min. 2 Zeichen für schulweite Suche"}
                    </div>
                  )}
                {filteredClassCandidates.length > 0 && (
                  <>
                    <div className="px-3 py-1 text-[10px] uppercase tracking-wider font-semibold text-blue-700 bg-blue-50">
                      Aus Klassenbezug
                    </div>
                    {filteredClassCandidates.map((c) => (
                      <button
                        key={`class-${c.id}`}
                        type="button"
                        onClick={() => toggleGuestSelection(c)}
                        className="block w-full text-left px-3 py-1.5 text-sm hover:bg-blue-50"
                      >
                        {c.name}
                        {c.className && (
                          <span className="ml-1 text-xs text-gray-500 font-normal">
                            ({c.className})
                          </span>
                        )}
                        {/* Audit #25: warn when this student already has
                            another module at the same time slot. */}
                        {c.currentModuleName && (
                          <span className="ml-2 text-xs text-amber-700 font-normal italic">
                            bereits in: {c.currentModuleName}
                          </span>
                        )}
                      </button>
                    ))}
                  </>
                )}
                {searchCandidates.length > 0 && (
                  <>
                    <div className="px-3 py-1 text-[10px] uppercase tracking-wider font-semibold text-blue-700 bg-blue-50 border-t border-blue-100">
                      Schulweite Suche
                    </div>
                    {searchCandidates.map((c) => (
                      <button
                        key={`search-${c.id}`}
                        type="button"
                        onClick={() => toggleGuestSelection(c)}
                        className="block w-full text-left px-3 py-1.5 text-sm hover:bg-blue-50"
                      >
                        {c.name}
                        {c.className && (
                          <span className="ml-1 text-xs text-gray-500 font-normal">
                            ({c.className})
                          </span>
                        )}
                        {/* Audit #25 (see Klassenbezug block). */}
                        {c.currentModuleName && (
                          <span className="ml-2 text-xs text-amber-700 font-normal italic">
                            bereits in: {c.currentModuleName}
                          </span>
                        )}
                      </button>
                    ))}
                  </>
                )}
              </div>
            )}
          </div>

          <div>
            <Label
              htmlFor="guest-reason"
              className="text-xs font-medium text-blue-900"
            >
              Grund <span className="text-red-600">*</span>
            </Label>
            <Textarea
              id="guest-reason"
              value={guestReason}
              onChange={(e) => setGuestReason(e.target.value)}
              placeholder="z. B. kann heute nicht zum Schwimmen"
              rows={2}
              className="mt-1 min-h-[60px] bg-white"
              disabled={isAddingGuest}
            />
          </div>

          <div className="flex justify-end">
            <Button
              type="button"
              onClick={requestAddGuestStudents}
              disabled={!canSubmitGuests}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {isAddingGuest
                ? "Wird hinzugefügt…"
                : selectedGuests.length > 1
                  ? `Als Tagesgäste hinzufügen (${selectedGuests.length})`
                  : "Als Tagesgast hinzufügen"}
            </Button>
          </div>
          {guestError && (
            <div className="text-xs text-red-600">{guestError}</div>
          )}
        </div>
      )}

      {/* Audit #25: confirmation dialog when one or more picked guests
          already have a module at this same time slot. Confirming will
          add them as guests AND mark them absent_excused on the original
          module's lesson with a standardised note. */}
      <AlertDialog
        open={conflictConfirmOpen}
        onOpenChange={setConflictConfirmOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {conflictGuests.length > 1
                ? "Mehrere Schüler:innen haben bereits ein Modul"
                : "Schüler:in hat bereits ein Modul"}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm">
                <p>
                  Folgende Auswahl ist bereits zur gleichen Zeit in einem
                  anderen Modul eingetragen:
                </p>
                <ul className="list-disc pl-5">
                  {conflictGuests.map((g) => (
                    <li key={`conflict-${g.id}`}>
                      <span className="font-medium">{g.name}</span>{" "}
                      <span className="text-gray-500">
                        — bereits in: {g.currentModuleName}
                      </span>
                    </li>
                  ))}
                </ul>
                <p>
                  Beim Hinzufügen wird die Person aus dem ursprünglichen
                  Modul mit der Notiz „Intern beurlaubt, Gast im Modul:{" "}
                  {lessonName?.trim() || "anderes Modul"}" als entschuldigt
                  abwesend markiert.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setConflictConfirmOpen(false);
                void handleAddGuestStudents();
              }}
            >
              Trotzdem hinzufügen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
